import { useMemo, useState } from 'react'
import Breadcrumbs from '@/components/Breadcrumbs'
import DisclaimerNote from '@/components/DisclaimerNote'
import FaqAccordion from '@/components/FaqAccordion'
import NumberInput from '@/components/ui/NumberInput'
import SectionHeading from '@/components/ui/SectionHeading'
import StatCard from '@/components/ui/StatCard'
import Seo from '@/components/Seo'
import { formatCurrency } from '@/lib/format'
import { breadcrumbSchema, faqPageSchema, softwareApplicationSchema } from '@/lib/schema'

const faqItems = [
  { question: 'Why are Solana mint costs so much lower than Ethereum?', answer: 'Solana uses a fundamentally different fee structure with fixed, tiny transaction fees (typically 0.000005 SOL) rather than a Gwei-based gas auction, making it far cheaper for high-volume mints.' },
  { question: 'What is rent-exempt reserve on Solana?', answer: 'Solana requires a small SOL deposit to keep NFT metadata and token accounts rent-exempt on-chain. This is usually a fraction of a SOL and is often recoverable when closing the account.' },
  { question: 'Do Solana NFT mints have gas price volatility?', answer: 'Solana fees are far more stable than Ethereum, but can rise during network congestion. Priority fees can be added to speed up transaction inclusion during high demand.' },
]

function SolanaMintCost() {
  const [mintPrice, setMintPrice] = useState(0.5)
  const [quantity, setQuantity] = useState(1)
  const [txFee, setTxFee] = useState(0.000005)
  const [priorityFee, setPriorityFee] = useState(0.0001)
  const [rentReserve, setRentReserve] = useState(0.0021)
  const [solPrice, setSolPrice] = useState(150)
  const [marketplaceFeePct, setMarketplaceFeePct] = useState(2)

  const results = useMemo(() => {
    const feePerMint = txFee + priorityFee + rentReserve
    const totalFeesSol = feePerMint * quantity
    const totalMintSol = mintPrice * quantity
    const totalSol = totalMintSol + totalFeesSol
    const totalUsd = totalSol * solPrice
    const costPerItemUsd = totalUsd / quantity
    const breakevenFloor = costPerItemUsd / (1 - marketplaceFeePct / 100)
    return { feePerMint, totalFeesSol, totalSol, totalUsd, costPerItemUsd, breakevenFloor }
  }, [mintPrice, quantity, txFee, priorityFee, rentReserve, solPrice, marketplaceFeePct])

  return (
    <>
      <Seo
        title="Solana Mint Cost Calculator - Transaction Fee Estimator"
        description="Free Solana NFT mint cost calculator. Estimate transaction fees, priority fees, rent-exempt reserve, and breakeven resale floor price for Solana mints."
        path="/calculators/solana-mint-cost"
        schema={[
          softwareApplicationSchema('Solana Mint Cost Calculator', 'Estimate Solana NFT minting transaction costs and breakeven price.', 'https://www.mintcalculator.com/calculators/solana-mint-cost'),
          faqPageSchema(faqItems),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Solana Mint Cost Calculator', path: '/calculators/solana-mint-cost' }]),
        ]}
      />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <Breadcrumbs items={[{ name: 'Home', path: '/' }, { name: 'Solana Mint Cost Calculator', path: '/calculators/solana-mint-cost' }]} />
        <SectionHeading
          eyebrow="Web3 & Crypto Mint Gas"
          title="Solana Mint Cost Calculator"
          description="Estimate your total Solana mint cost including transaction fees, priority fees, and rent-exempt reserves."
        />

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-5">
          <div className="space-y-5 rounded-xl border border-slate-800 bg-slate-900/50 p-5 lg:col-span-2">
            <NumberInput label="Mint price (SOL)" value={mintPrice} onChange={setMintPrice} step={0.01} />
            <NumberInput label="Mint quantity" value={quantity} onChange={setQuantity} step={1} />
            <NumberInput label="Base transaction fee (SOL)" value={txFee} onChange={setTxFee} step={0.000001} accent="cyan" />
            <NumberInput label="Priority fee (SOL)" value={priorityFee} onChange={setPriorityFee} step={0.00001} accent="cyan" />
            <NumberInput label="Rent-exempt reserve (SOL)" value={rentReserve} onChange={setRentReserve} step={0.0001} accent="teal" />
            <NumberInput label="SOL USD spot price" value={solPrice} onChange={setSolPrice} prefix="$" step={1} accent="teal" />
            <NumberInput label="Marketplace fee %" value={marketplaceFeePct} onChange={setMarketplaceFeePct} suffix="%" step={0.5} accent="amber" />
          </div>

          <div className="grid grid-cols-2 gap-3 lg:col-span-3">
            <StatCard label="Total Mint Cost" value={`${results.totalSol.toFixed(6)} SOL`} accent="emerald" size="lg" sublabel={formatCurrency(results.totalUsd)} />
            <StatCard label="Total Fees" value={`${results.totalFeesSol.toFixed(6)} SOL`} accent="cyan" />
            <StatCard label="Cost Per Item" value={formatCurrency(results.costPerItemUsd)} accent="teal" />
            <StatCard label="Breakeven Floor Price" value={formatCurrency(results.breakevenFloor)} accent="amber" sublabel="After marketplace fees" />
          </div>
        </div>

        <div className="mt-6">
          <DisclaimerNote text="Solana network fees fluctuate slightly during congestion. Always confirm live fees in your wallet before minting." />
        </div>

        <div className="mt-16 max-w-3xl">
          <SectionHeading title="Frequently Asked Questions" />
          <div className="mt-6">
            <FaqAccordion items={faqItems} />
          </div>
        </div>
      </div>
    </>
  )
}

export default SolanaMintCost
